import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

// WAV hening 1 sampel — dipakai untuk "membuka kunci" audio iOS pada sentuhan pertama bila playlist belum termuat
const SILENT = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=";
const PREF_KEY = "jawsel_radio_on";
const MINI_KEY = "jawsel_radio_mini";

// Radio Jawsel di halaman kota: playlist aktif diputar berurutan & berulang; pengumuman yang sudah
// punya audio disisipkan tiap 2 lagu. Mulai otomatis pada sentuhan pertama (bila warga belum mematikan radio).
export function useRadio() {
  const audioRef = useRef(null);
  const queueRef = useRef([]);
  const idxRef = useRef(0);
  const errRef = useRef(0);
  const startedRef = useRef(false);
  const pendingRef = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [ready, setReady] = useState(false);
  const [on, setOn] = useState(() => localStorage.getItem(PREF_KEY) !== "0");
  const [playing, setPlaying] = useState(false);
  const [now, setNow] = useState(null);
  // Pil radio di peta kota dikecilkan jadi ikon bulat (pilihan diingat)
  const [mini, setMiniState] = useState(() => localStorage.getItem(MINI_KEY) === "1");
  const setMini = useCallback(v => { setMiniState(v); localStorage.setItem(MINI_KEY, v ? "1" : "0"); }, []);

  const audio = () => {
    if (!audioRef.current) { const a = new Audio(); a.preload = "auto"; audioRef.current = a; }
    return audioRef.current;
  };

  // Jam siaran bersama: offset jam klien → server, epoch & total jadwal dari /api/radio
  const siaranRef = useRef({ offset: 0, epoch: 0, total: 0 });

  const playIndex = useCallback((i, seek = 0) => {
    const q = queueRef.current;
    if (!q.length) return false;
    const k = ((i % q.length) + q.length) % q.length;
    idxRef.current = k;
    const a = audio();
    a.src = q[k].url;
    if (seek > 0.5) { const atur = () => { try { a.currentTime = seek; } catch { /* abaikan */ } }; a.addEventListener("loadedmetadata", atur, { once: true }); }
    setNow(q[k]);
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    return true;
  }, []);

  // Posisi siaran saat ini (semua pendengar sama): detik sejak epoch mod total → item & detik di dalam item
  const posisiSiaran = useCallback(() => {
    const { offset, epoch, total } = siaranRef.current, q = queueRef.current;
    if (!q.length || !total) return { i: 0, seek: 0 };
    const t = ((Date.now() / 1000 + offset - epoch) % total + total) % total;
    const i = Math.max(0, q.findIndex((it, n) => t >= it.mulai && t < it.mulai + it.durasi));
    return { i, seek: t - q[i].mulai };
  }, []);
  const sinkron = useCallback(() => { const { i, seek } = posisiSiaran(); return playIndex(i, seek); }, [posisiSiaran, playIndex]);

  // Muat jadwal siaran (server menyusun urutan + durasi); segarkan tiap 60 dtk agar pengumuman baru ikut masuk
  useEffect(() => {
    let alive = true;
    const muat = () => api.publicRadio().then(d => {
      if (!alive) return;
      const q = (d.jadwal || []).map(it => ({ ...it, url: api.mediaUrl(it.url) }));
      const gantiJadwal = JSON.stringify(q.map(x => x.id)) !== JSON.stringify(queueRef.current.map(x => x.id));
      queueRef.current = q;
      siaranRef.current = { offset: d.server_now - Date.now() / 1000, epoch: d.epoch, total: d.total };
      setReady(q.length > 0);
      setLoaded(true);
      if (pendingRef.current && q.length) { pendingRef.current = false; sinkron(); }
      else if (gantiJadwal && startedRef.current && audioRef.current && !audioRef.current.paused) sinkron(); // jadwal berubah saat mengudara → selaraskan lagi
    }).catch(() => { if (alive) setLoaded(true); });
    muat();
    const h = setInterval(muat, 60000);
    return () => { alive = false; clearInterval(h); };
  }, [sinkron]);

  // Event pemutar: lagu selesai → berikutnya; gagal → lewati (berhenti bila semua gagal)
  useEffect(() => {
    const a = audio();
    const onEnd = () => sinkron(); // item selesai → ikuti jam siaran (biasanya item berikutnya dari awal)
    const onErr = () => {
      errRef.current += 1;
      if (errRef.current < queueRef.current.length) playIndex(idxRef.current + 1); else setPlaying(false);
    };
    const onPlay = () => { setPlaying(true); errRef.current = 0; };
    const onPause = () => setPlaying(false);
    a.addEventListener("ended", onEnd); a.addEventListener("error", onErr); a.addEventListener("playing", onPlay); a.addEventListener("pause", onPause);
    return () => {
      a.removeEventListener("ended", onEnd); a.removeEventListener("error", onErr); a.removeEventListener("playing", onPlay); a.removeEventListener("pause", onPause);
      a.pause(); a.removeAttribute("src");
    };
  }, [playIndex, sinkron]);

  const start = useCallback(() => {
    if (queueRef.current.length) return sinkron();
    // playlist belum termuat: buka kunci audio dalam gestur ini, putar begitu data tiba
    const a = audio(); a.src = SILENT; a.play().catch(() => {});
    pendingRef.current = true;
    return false;
  }, [playIndex]);

  // Dipanggil pada sentuhan pertama di peta (dalam gestur pengguna → autoplay diizinkan)
  const onFirstTouch = useCallback(() => {
    if (startedRef.current || !on) return;
    startedRef.current = true;
    start();
  }, [on, start]);

  const toggle = useCallback(() => {
    startedRef.current = true;
    if (playing) {
      audio().pause(); setOn(false); localStorage.setItem(PREF_KEY, "0"); pendingRef.current = false;
      return;
    }
    setOn(true); localStorage.setItem(PREF_KEY, "1");
    start(); // radio live: menyala lagi = ikut posisi siaran saat ini, bukan melanjutkan yang tadi
  }, [playing, start]);

  // Untuk uji sinkron antar-browser (dan debug): judul & detik posisi item yang sedang mengudara
  useEffect(() => { window.__radio = { now, posisi: () => ({ ...posisiSiaran(), judul: queueRef.current[posisiSiaran().i]?.title, ct: audioRef.current?.currentTime }) }; }, [now, posisiSiaran]);
  return { on, playing, now, ready, loaded, mini, setMini, count: queueRef.current.filter(x => x.kind === "lagu").length, toggle, onFirstTouch };
}
